/**
 * Player Profile Page
 * Shows individual player stats, VoD library, and match history
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, getDocs } from 'firebase/firestore';
import type { PlayerRosterDocument, ScoutingReportDocument, EventVodDocument } from '@schemas/firestore';

// Game definitions
const GAMES = [
    { id: 'smashSolo', name: 'Smash Solo', icon: '🎮', color: 'bg-red-500' },
    { id: 'smashCrews', name: 'Smash Crews', icon: '👥', color: 'bg-red-600' },
    { id: 'smashJVCrews', name: 'Smash JV Crews', icon: '🛡️', color: 'bg-amber-500' },

    { id: 'madden', name: 'Madden', icon: '🏈', color: 'bg-orange-500' },
    { id: 'fortnite', name: 'Fortnite', icon: '🎯', color: 'bg-purple-500' },
];

interface ReportWithId extends ScoutingReportDocument {
    id: string;
}

interface VodWithId extends EventVodDocument {
    id: string;
}

export default function PlayerProfilePage() {
    const { playerId } = useParams<{ playerId: string }>();
    const { userDoc } = useAuth();

    const [player, setPlayer] = useState<PlayerRosterDocument | null>(null);
    const [reports, setReports] = useState<ReportWithId[]>([]);
    const [vods, setVods] = useState<VodWithId[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'overview' | 'vods' | 'intel'>('overview');

    // Check if viewing own profile
    const isOwnProfile = userDoc?.linkedPlayerId === playerId;

    // Get player's games
    const playerGames = player ? GAMES.filter(g =>
        player.games?.[g.id as keyof typeof player.games]
    ) : [];

    // Load player data
    useEffect(() => {
        async function loadData() {
            if (!playerId) return;

            try {
                // Get player roster entry
                const rosterSnap = await getDocs(collection(db, 'playerRoster'));
                const playerDoc = rosterSnap.docs.find(d => d.id === playerId);

                if (playerDoc) {
                    setPlayer({ id: playerDoc.id, ...playerDoc.data() } as unknown as PlayerRosterDocument);
                }

                // Get scouting reports by this player
                const reportsSnap = await getDocs(collection(db, 'scoutingReports'));
                const playerReports = reportsSnap.docs
                    .filter(d => {
                        const data = d.data();
                        // Reports created by this player's linked user
                        return data.createdBy === playerDoc?.data()?.linkedUserId;
                    })
                    .map(d => ({ id: d.id, ...d.data() } as ReportWithId));
                setReports(playerReports);

                // Get VoDs uploaded by this player or team VoDs
                const vodsSnap = await getDocs(collection(db, 'eventVods'));
                const playerVods = vodsSnap.docs
                    .filter(d => {
                        const data = d.data();
                        return data.uploadedBy === playerDoc?.data()?.linkedUserId ||
                            data.associationType === 'team';
                    })
                    .map(d => ({ id: d.id, ...d.data() } as VodWithId));
                setVods(playerVods);

            } catch (err) {
                console.error('Error loading player data:', err);
            } finally {
                setLoading(false);
            }
        }

        loadData();
    }, [playerId]);

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!player) {
        return (
            <div className="p-8 text-center">
                <h1 className="text-2xl font-bold text-white mb-2">Player Not Found</h1>
                <p className="text-secondary-400">This player profile doesn't exist.</p>
                <Link to="/app" className="btn-primary mt-4 inline-block">
                    Back to Dashboard
                </Link>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 lg:p-8 max-w-5xl">
            {/* Profile Header */}
            <div className="card mb-6">
                <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-4 md:gap-0">
                    <div className="flex flex-col md:flex-row items-center gap-4 text-center md:text-left">
                        {/* Avatar */}
                        <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center shrink-0">
                            <span className="text-3xl font-bold text-white">
                                {player.firstName?.[0]}{player.lastName?.[0]}
                            </span>
                        </div>

                        {/* Info */}
                        <div>
                            <h1 className="text-2xl font-bold text-white flex items-center justify-center md:justify-start gap-2">
                                {player.firstName} {player.lastName}
                                {Object.values(player.isCaptain || {}).some(v => v) && (
                                    <span title="Team Captain">👑</span>
                                )}
                            </h1>

                            {player.gamerTag && (
                                <p className="text-primary-400 font-medium">@{player.gamerTag}</p>
                            )}

                            <p className="text-secondary-400">
                                Grade {player.grade} • {player.status === 'active' ? '✅ Active' : player.status}
                            </p>

                            {/* Discord */}
                            {player.discordUsername && (
                                <p className="text-secondary-500 text-sm mt-1">
                                    💬 {player.discordUsername}
                                </p>
                            )}
                        </div>
                    </div>

                    {isOwnProfile && (
                        <span className="text-xs px-2 py-1 bg-primary-500/20 text-primary-400 rounded self-center md:self-start">
                            Your Profile
                        </span>
                    )}
                </div>

                {/* Games */}
                <div className="mt-6 pt-4 border-t border-secondary-700">
                    <h3 className="text-sm font-semibold text-secondary-400 uppercase tracking-wider mb-3">
                        Teams
                    </h3>
                    <div className="flex flex-wrap gap-2">
                        {playerGames.map(game => {
                            const isCaptain = player.isCaptain?.[game.id as keyof typeof player.isCaptain];
                            return (
                                <div
                                    key={game.id}
                                    className={`${game.color} px-3 py-1.5 rounded-lg flex items-center gap-2 text-white text-sm font-medium`}
                                >
                                    <span>{game.icon}</span>
                                    {game.name}
                                    {isCaptain && <span>👑</span>}
                                </div>
                            );
                        })}
                        {playerGames.length === 0 && (
                            <p className="text-secondary-500">No teams assigned</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 md:pb-0 scrollbar-hide">
                {(['overview', 'vods', 'intel'] as const).map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeTab === tab
                            ? 'bg-primary-500 text-white'
                            : 'bg-secondary-800 text-secondary-300 hover:bg-secondary-700'
                            }`}
                    >
                        {tab === 'overview' && '📊 Overview'}
                        {tab === 'vods' && `📹 VoDs (${vods.length})`}
                        {tab === 'intel' && `🔍 Intel (${reports.length})`}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
                <div className="grid md:grid-cols-2 gap-4">
                    {/* Stats Card */}
                    <div className="card">
                        <h3 className="text-lg font-semibold text-white mb-4">Quick Stats</h3>
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-secondary-400">Games Played</span>
                                <span className="text-white font-medium">{playerGames.length}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-secondary-400">VoDs Uploaded</span>
                                <span className="text-white font-medium">{vods.length}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-secondary-400">Intel Reports</span>
                                <span className="text-white font-medium">{reports.length}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-secondary-400">Captain Of</span>
                                <span className="text-yellow-400 font-medium">
                                    {Object.entries(player.isCaptain || {}).filter(([, v]) => v).length} team(s)
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Recent Activity */}
                    <div className="card">
                        <h3 className="text-lg font-semibold text-white mb-4">Recent Activity</h3>
                        {reports.length === 0 && vods.length === 0 ? (
                            <p className="text-secondary-500">No recent activity</p>
                        ) : (
                            <div className="space-y-3">
                                {reports.slice(0, 3).map(report => (
                                    <div key={report.id} className="flex items-center gap-3 p-2 bg-secondary-800/50 rounded">
                                        <span className="text-yellow-400">📝</span>
                                        <div>
                                            <p className="text-sm text-white">
                                                Intel on {report.opponentName}
                                            </p>
                                            <p className="text-xs text-secondary-500">
                                                {report.game}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                                {vods.slice(0, 2).map(vod => (
                                    <div key={vod.id} className="flex items-center gap-3 p-2 bg-secondary-800/50 rounded">
                                        <span className="text-purple-400">📹</span>
                                        <div>
                                            <p className="text-sm text-white">{vod.title}</p>
                                            <p className="text-xs text-secondary-500">{vod.game}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'vods' && (
                <div className="card">
                    <h3 className="text-lg font-semibold text-white mb-4">VoD Library</h3>
                    {vods.length === 0 ? (
                        <p className="text-secondary-500 py-4 text-center">No VoDs uploaded yet</p>
                    ) : (
                        <div className="space-y-3">
                            {vods.map(vod => (
                                <a
                                    key={vod.id}
                                    href={vod.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-between p-3 bg-secondary-800/50 rounded-lg hover:bg-secondary-800 transition-colors"
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="text-2xl text-purple-400">📹</span>
                                        <div>
                                            <p className="font-medium text-white">{vod.title}</p>
                                            <p className="text-sm text-secondary-400">{vod.game}</p>
                                        </div>
                                    </div>
                                    <svg className="w-5 h-5 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                </a>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'intel' && (
                <div className="card">
                    <h3 className="text-lg font-semibold text-white mb-4">Intel Contributions</h3>
                    {reports.length === 0 ? (
                        <p className="text-secondary-500 py-4 text-center">No intel reports yet</p>
                    ) : (
                        <div className="space-y-3">
                            {reports.map(report => (
                                <div
                                    key={report.id}
                                    className="p-3 bg-secondary-800/50 rounded-lg"
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <h4 className="font-medium text-white">
                                            {report.opponentName}
                                            {report.opponentTeam && (
                                                <span className="text-secondary-400 ml-2">({report.opponentTeam})</span>
                                            )}
                                        </h4>
                                        <span className="text-xs px-2 py-0.5 bg-primary-500/20 text-primary-400 rounded">
                                            {report.game}
                                        </span>
                                    </div>
                                    <p className="text-sm text-secondary-300 line-clamp-2">
                                        {report.strategies}
                                    </p>
                                    {report.tags && report.tags.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {report.tags.map((tag, i) => (
                                                <span key={i} className="text-xs px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 rounded">
                                                    #{tag}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
